import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { circleIndex } from "@/theory/notes";
import { NextUp } from "@/components/Callouts";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory9() {
  const { lesson, course, accent } = findLesson("music-theory", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT9-Circle-of-Fifths-Animated.mp4"
        caption="The Circle of Fifths — built a fifth at a time"
        hint="≈ 47 sec · watch first"
      />
      <Chapter number="09" eyebrow="Music Theory · Chapter Nine" title="The circle of fifths — a map of every key">
        <p>
          The circle of fifths is the single most useful diagram in Western music theory. It
          shows you all twelve roots arranged so that each step around the circle is a
          <strong> perfect fifth</strong>. Walk clockwise: every step adds a sharp. Walk
          counter-clockwise: every step adds a flat. Twelve steps in either direction and you
          arrive back where you started — every key on Earth visited along the way.
        </p>
        <MTQuote>
          It's something which will help you with your scale knowledge. It will help you to
          combine scales together, link them up. It will also help you resolve from chord to
          chord. It's great to learn intervals. It's a great tool to develop chord
          progressions which are both popular as well as not so popular.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="How it's drawn" title="C at twelve o'clock, then climb">
        <Prose>
          <p>
            Place <strong>C</strong> at twelve o'clock. Walk clockwise, jumping a perfect fifth
            (seven semitones) every step. The notes fall into a clean, almost magical order —
            and the diagram naturally hits the twelve hour positions of a clock face exactly.
          </p>
        </Prose>

        <Steps>
          <Step><strong>12 o'clock — C</strong></Step>
          <Step><strong>1 — G.</strong> C's fifth.</Step>
          <Step><strong>2 — D.</strong> G's fifth.</Step>
          <Step><strong>3 — A.</strong> D's fifth.</Step>
          <Step><strong>4 — E.</strong> A's fifth.</Step>
          <Step><strong>5 — B.</strong> E's fifth.</Step>
          <Step><strong>6 — F♯ / G♭.</strong> The midpoint — the tritone of C. Same key on the piano, two spellings.</Step>
          <Step><strong>7 — D♭ / C♯.</strong></Step>
          <Step><strong>8 — A♭.</strong></Step>
          <Step><strong>9 — E♭.</strong></Step>
          <Step><strong>10 — B♭.</strong></Step>
          <Step><strong>11 — F.</strong> One more fifth and you're back to C.</Step>
        </Steps>

        <Prose>
          <p>
            One subtlety worth memorising: B's fifth is <strong>F♯</strong>, not F. (B → F is
            only six semitones — a tritone, not a perfect fifth.) That's where the sharps start
            piling up, and that's why the right side of the circle gets sharp names while the
            left side gets flat names.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths size={360} />
        </div>
      </Chapter>

      <Chapter eyebrow="Two directions, two intervals" title="Clockwise = 5ths, counter-clockwise = 4ths">
        <Prose>
          <p>
            Walking <em>clockwise</em> moves you up by a perfect 5th every step. Walking
            <em> counter-clockwise</em> moves you up by a perfect 4th — which is the same as
            moving down by a perfect 5th. So the circle is two interval ladders for the price of
            one diagram. Most people memorise it in one direction and stop. Don't.
          </p>
        </Prose>

        <TheoryBox label="The two readings of the circle">
          <div className="text-center font-mt text-lg text-amber-200">
            <strong>Clockwise: +P5 &nbsp;·&nbsp; Counter-clockwise: +P4</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (every perfect fifth and every perfect fourth on every root, on one diagram)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Counter-clockwise from C: <span className="font-mono text-amber-300">C → F → B♭ → E♭ → A♭ → D♭ → G♭ → B → E → A → D → G → C</span>.
            Each step is a perfect fourth. Notice the spelling shift halfway: a fourth above G♭
            is C♭, which we write as its enharmonic twin <strong>B</strong> rather than pile on
            a seventh flat. Same trip, opposite direction.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A worked climb" title="C → G → D → A: three fifths up the ladder">
        <Prose>
          <p>
            Let's actually <em>climb</em> the circle. Start on C. Add a fifth: you arrive at G.
            Add another: D. Add another: A. Each note is a perfect fifth above the previous one,
            and on the diagram the highlight just steps clockwise around the dial.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            highlights={[
              { index: idx("C"), color: "#22c55e", label: "Start" },
              { index: idx("G"), color: "#f59e0b", label: "+P5" },
              { index: idx("D"), color: "#f59e0b", label: "+P5" },
              { index: idx("A"), color: "#3b82f6", label: "+P5" },
            ]}
          />
        </div>

        <Prose>
          <p>
            Continue and you'll visit every key on the piano: A → E → B → F♯ → C♯ → A♭ → E♭ →
            B♭ → F → C. Twelve fifths, twelve roots, one closed loop — and there is a lovely
            catch in why it closes. Twelve <em>pure</em> fifths land very slightly ABOVE seven
            octaves; the gap is about a quarter of a semitone and is called the Pythagorean
            comma. Modern tuning shaves roughly two cents off every fifth so the twelve of them
            land exactly on seven octaves. The circle doesn't close by itself — we close it,
            and that compromise is what lets you play in all twelve keys on one piano.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The descent" title="C → F → B♭ → E♭: three fourths down">
        <Prose>
          <p>
            Now walk counter-clockwise from C. Each step adds a perfect fourth (or, equivalently,
            drops a perfect fifth). The first three stops:
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            highlights={[
              { index: idx("C"), color: "#22c55e", label: "Start" },
              { index: idx("F"), color: "#a78bfa", label: "+P4" },
              { index: idx("Bb"), color: "#a78bfa", label: "+P4" },
              { index: idx("Eb"), color: "#3b82f6", label: "+P4" },
            ]}
          />
        </div>

        <Prose>
          <p>
            <span className="font-mono text-amber-300">C → F → B♭ → E♭</span>. Each step is a
            perfect fourth up. The flats arrive in a strict order: F has one (B♭); B♭ has two
            (B♭, E♭); E♭ has three (B♭, E♭, A♭). This pattern — sharps clockwise, flats
            counter-clockwise, both adding one at a time — means the circle of fifths is also a
            <strong> key-signature cheat sheet</strong>.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Why this is so useful" title="Fifths are the natural chord motion">
        <Prose>
          <p>
            Beyond memorising key signatures, the circle is a chord-progression generator. One
            of the most important chord motions in tonal music is the <strong>V → I</strong>
            cadence — a perfect fifth resolving down to the root. That's a counter-clockwise
            step on the circle. Stack two of those moves together and you have
            <strong> ii → V → I</strong>, the spine of jazz harmony. Stack four or five and
            you have the bones of a whole progression.
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>I → V</strong> — clockwise step (one of the most common moves in classical music).</li>
            <li><strong>V → I</strong> — counter-clockwise step (the famous "perfect cadence").</li>
            <li><strong>ii → V → I</strong> — two counter-clockwise steps in a row (jazz).</li>
            <li><strong>vi → ii → V → I</strong> — four counter-clockwise steps in a row (the classic "circle progression").</li>
          </ul>
        </Prose>

        <Prose>
          <p>
            Composers from Bach to Bacharach to Billie Eilish use the same geometry: the circle
            of fifths is what makes successive chord changes feel natural to the ear. Walk the
            circle counter-clockwise and you walk through history.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The exercise" title="Climb in fifths, descend in fourths">
        <Prose>
          <p>
            The simplest, most musical practice you can do with the circle is to play it on the
            piano. Choose any root (start on C). Play the root, then jump a perfect fifth up.
            Play that, then jump a perfect fifth up. Keep going until you cycle back — twelve
            jumps, seven octaves climbed.
          </p>
        </Prose>

        <Steps>
          <Step>
            <strong>Climb in fifths (clockwise):</strong>
            C → G → D → A → E → B → F♯ → C♯ → A♭ → E♭ → B♭ → F → C.
          </Step>
          <Step>
            <strong>Descend in fourths (counter-clockwise):</strong>
            C → F → B♭ → E♭ → A♭ → D♭ → G♭ → B → E → A → D → G → C.
          </Step>
          <Step>
            <strong>Use sharp names going up, flat names going down.</strong> That single rule
            keeps your spellings consistent with how key signatures are actually written.
          </Step>
          <Step>
            <strong>Then practise scales in the same order.</strong> C major, then G major, then
            D major. Each new scale has exactly one more sharp than the previous one — the
            circle teaches you that rule by walking it.
          </Step>
        </Steps>

        <Prose>
          <p>
            Once this trip is in your hands, the circle stops being a diagram and starts being
            a tool. Future chapters will use it for chord progressions, modulation, key
            signatures, and improvisation. Get the climbing and descending solid now and the
            rest of the course unlocks easily.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/09a-circle-of-fifths-intro.png"
          caption="Jason's circle of fifths with the '4-1-5-2-6-3-7' interval lookup written in the corner. Starting from any tonic, the neighbours on the circle give you P4 (counter-clockwise) and P5 (clockwise); two more steps clockwise reveal M2 and M6; the next two reveal M3 and M7. This is the key to instant interval recall."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-10"
        label="Chapter 10 · Circle of Fifths - Key Signatures (Sharps)"
        why="With the climb in your hands, the next move is to use the circle to decode key signatures — and see why each clockwise step around the circle adds exactly one more sharp."
      />
    </LessonShell>
  );
}
